import { useState, useEffect } from 'react';

interface OrderFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  formAction: string;
}

const QUANTITIES = [6, 12, 24, 48, 96];

const PRICE_MAP: Record<number, number> = {
  6: 39,
  12: 78,
  24: 156,
  48: 312,
  96: 624,
};

export default function OrderFormModal({ isOpen, onClose, productName, formAction }: OrderFormModalProps) {
  const [selectedQuantity, setSelectedQuantity] = useState<number>(6);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const resetForm = () => {
    setSelectedQuantity(6);
    setName('');
    setEmail('');
    setPhone('');
    setAddress('');
    setNotes('');
    setError('');
    setSubmitted(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!name.trim() || !email.trim() || !phone.trim() || !address.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    if (notes.length > 500) {
      setError('Notes must be 500 characters or less.');
      return;
    }

    setSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('Product', productName);
      formData.append('Quantity', String(selectedQuantity));
      formData.append('name', name.trim());
      formData.append('email', email.trim());
      formData.append('Phone', phone.trim());
      formData.append('Shipping Address', address.trim());
      formData.append('Notes', notes.trim());

      const response = await fetch(formAction, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose}></div>
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#2C5F2D] rounded-t-3xl px-6 py-5 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Georgia, serif' }}>
              Order {productName}
            </h2>
            <p className="text-[#FFD700] text-sm mt-1">Fill out the form and we'll get back to you!</p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center text-white hover:text-[#FFD700] transition-colors cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-[#2C5F2D] rounded-full mx-auto mb-4">
              <i className="ri-check-line text-3xl text-[#FFD700]"></i>
            </div>
            <h3 className="text-2xl font-bold text-[#2C5F2D] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              Order Submitted!
            </h3>
            <p className="text-[#666] mb-2">
              Thank you for your order of <strong>{selectedQuantity} {productName}</strong>.
            </p>
            <p className="text-[#666] mb-6">
              We'll review your order and reach out to you shortly via email.
            </p>
            <button
              onClick={handleClose}
              className="bg-[#FFD700] text-[#2C5F2D] px-8 py-3 rounded-full font-bold hover:bg-[#FFC700] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Close
            </button>
          </div>
        ) : (
          <form
            id={`order-form-${productName.replace(/\s+/g, '-').toLowerCase()}`}
            data-readdy-form
            onSubmit={handleSubmit}
            className="p-6 space-y-5"
          >
            {/* Quantity Selection */}
            <div>
              <label className="block text-sm font-semibold text-[#2C5F2D] mb-2">
                Select Quantity <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-5 gap-2">
                {QUANTITIES.map((qty) => (
                  <button
                    key={qty}
                    type="button"
                    onClick={() => setSelectedQuantity(qty)}
                    className={`py-3 rounded-xl text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      selectedQuantity === qty
                        ? 'bg-[#2C5F2D] text-[#FFD700] shadow-lg scale-105'
                        : 'bg-[#FFF8E7] text-[#2C5F2D] hover:bg-[#FFD700]/30'
                    }`}
                  >
                    {qty}
                  </button>
                ))}
              </div>
              <input type="hidden" name="Quantity" value={selectedQuantity} />
            </div>

            {/* Price Display */}
            <div className="bg-[#FFF8E7] border border-[#2C5F2D]/15 rounded-xl px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="ri-price-tag-3-line text-[#2C5F2D] w-5 h-5 flex items-center justify-center"></i>
                <span className="text-sm text-[#2C5F2D] font-medium">
                  {selectedQuantity} bottles × $6.50 each
                </span>
              </div>
              <span className="text-xl font-bold text-[#2C5F2D]" style={{ fontFamily: 'Georgia, serif' }}>
                ${PRICE_MAP[selectedQuantity]?.toFixed(2)} USD
              </span>
            </div>

            {/* Name */}
            <div>
              <label htmlFor="order-name" className="block text-sm font-semibold text-[#2C5F2D] mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                id="order-name"
                name="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                className="w-full px-4 py-3 rounded-xl border border-[#2C5F2D]/20 bg-[#FFF8E7] text-sm text-[#333] focus:outline-none focus:ring-2 focus:ring-[#2C5F2D]/40 transition-all"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="order-email" className="block text-sm font-semibold text-[#2C5F2D] mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="order-email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-4 py-3 rounded-xl border border-[#2C5F2D]/20 bg-[#FFF8E7] text-sm text-[#333] focus:outline-none focus:ring-2 focus:ring-[#2C5F2D]/40 transition-all"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="order-phone" className="block text-sm font-semibold text-[#2C5F2D] mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                id="order-phone"
                name="Phone"
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full px-4 py-3 rounded-xl border border-[#2C5F2D]/20 bg-[#FFF8E7] text-sm text-[#333] focus:outline-none focus:ring-2 focus:ring-[#2C5F2D]/40 transition-all"
              />
            </div>

            {/* Shipping Address */}
            <div>
              <label htmlFor="order-address" className="block text-sm font-semibold text-[#2C5F2D] mb-1">
                Shipping Address <span className="text-red-500">*</span>
              </label>
              <input
                id="order-address"
                name="Shipping Address"
                type="text"
                required
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Full shipping address"
                className="w-full px-4 py-3 rounded-xl border border-[#2C5F2D]/20 bg-[#FFF8E7] text-sm text-[#333] focus:outline-none focus:ring-2 focus:ring-[#2C5F2D]/40 transition-all"
              />
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="order-notes" className="block text-sm font-semibold text-[#2C5F2D] mb-1">
                Additional Notes
              </label>
              <textarea
                id="order-notes"
                name="Notes"
                value={notes}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setNotes(e.target.value);
                  }
                }}
                maxLength={500}
                rows={3}
                placeholder="Any special requests or instructions..."
                className="w-full px-4 py-3 rounded-xl border border-[#2C5F2D]/20 bg-[#FFF8E7] text-sm text-[#333] focus:outline-none focus:ring-2 focus:ring-[#2C5F2D]/40 transition-all resize-none"
              />
              <p className="text-xs text-[#999] mt-1 text-right">{notes.length}/500</p>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-xl flex items-center gap-2">
                <i className="ri-error-warning-line w-5 h-5 flex items-center justify-center"></i>
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#FFD700] text-[#2C5F2D] py-4 rounded-full font-bold text-base hover:bg-[#FFC700] transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin w-5 h-5 flex items-center justify-center"></i>
                  Submitting...
                </span>
              ) : (
                `Submit Order — ${selectedQuantity} Units`
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
